import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import {
  registerSchema,
  validateForm,
} from "../../validations/authValidations";
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser } from "react-icons/fi";
import { RiShieldCheckFill } from "react-icons/ri";
import logo from "../../assets/image/logo.svg";
import loginImage from "../../assets/image/logo_shape.png";
import secureLine from "../../assets/image/logo-lines.svg";
import { ArrowRightIcon } from "lucide-react";

const Register = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated, userRole, isLoading } = useAuth();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "buyer",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (!isLoading && isAuthenticated && userRole) {
      // Redirect based on user role
      switch (userRole) {
        case "buyer":
          navigate("/buyer");
          break;
        case "seller":
          navigate("/seller");
          break;
        case "admin":
          navigate("/admin");
          break;
        case "super_admin":
          navigate("/super-admin");
          break;
        default:
          navigate("/login");
      }
    }
  }, [isAuthenticated, userRole, isLoading, navigate]);

  // Show loading while checking auth status
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Don't render register form if already authenticated
  if (isAuthenticated) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form using Yup schema
    const validation = await validateForm(registerSchema, formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API (remove confirmPassword, keep firstName and lastName separate)
      const { confirmPassword, ...apiData } = formData;

      // Use auth context register function
      const result = await register(apiData);

      if (result.success) {
        // Registration successful - redirect to login
        navigate("/login");
      } else {
        // Handle registration errors
        setErrors({ api: result.error });
      }
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage = "Network error. Please try again.";
      setErrors({ api: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  return (
    <div className="h-screen overflow-hidden flex font-inter">
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-end"
        style={{
          background: "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
        }}
      >
        <img
          src={secureLine}
          alt="ProtectPay"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
        <img
          src={loginImage}
          alt="ProtectPay"
          className="absolute top-5 inset-0 w-full h-full object-contain z-0"
        />
        <div className="relative max-w-[347px] mx-auto z-10 text-center pb-4 px-8">
          <h5 className="text-white font-normal text-xl font-rubik">
            Secure and accountable transactions.
          </h5>
          <p className="text-white font-[400] text-sm font-inter mt-2">
            Keep track of your sales and clients, in an all-in-one platform.
          </p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 bg-white flex overflow-y-scroll flex-col h-screen justify-between">
        <div className="absolute top-0 z-[0] left-0 w-full h-20 bg-blue-500 block lg:hidden"></div>
        <div className="flex-1 flex z-[1] py-14 items-center md:items-center justify-center">
          <div className="w-full max-w-full sm:max-w-lg md:max-w-lg 2xl:max-w-2xl">
            <div
              className="bg-white p-5 md:p-8 border-[0px] sm:border-[1px] sm:border-[#00000026]"
              style={{ borderRadius: "24px" }}
            >
              <Link to="/" className="flex items-center gap-2">
                <img
                  src={logo}
                  alt="ProtectPay"
                  className="items-center justify-center text-white w-[195px]"
                />
              </Link>

              <div className="mt-8 2xl:mt-16">
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 font-rubik">
                  Hi! welcome to ProtectPay!
                </h2>
                <p className="mt-4 text-sm text-gray-500">
                  Please create your account.
                </p>
              </div>

              {errors.api && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {errors.api}
                </div>
              )}

              <form className="mt-8 2xl:mt-16" onSubmit={handleSubmit}>
                <div className="space-y-3 2xl:space-y-6">
                  {/* Account Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      You are:
                    </label>
                    <select
                      name="role"
                      className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 px-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50"
                      value={formData.role}
                      onChange={handleChange}
                    >
                      <option value="">Please make your selection</option>
                      <option value="buyer">Buyer</option>
                      <option value="seller">Seller</option>
                    </select>
                  </div>

                  {/* Name Fields */}
                  <div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                            <FiUser className="h-5 w-5" />
                          </div>
                          <input
                            name="firstName"
                            type="text"
                            required
                            className={`block w-full rounded-lg border ${
                              errors.firstName
                                ? "border-red-300"
                                : "border-gray-300"
                            } bg-white py-2.5 pl-10 pr-3 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                            placeholder="Alexandra"
                            value={formData.firstName}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Last name
                        </label>
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                            <FiUser className="h-5 w-5" />
                          </div>
                          <input
                            name="lastName"
                            type="text"
                            required
                            className={`block w-full rounded-lg border ${
                              errors.lastName
                                ? "border-red-300"
                                : "border-gray-300"
                            } bg-white py-2.5 pl-10 pr-3 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                            placeholder="Last Name"
                            value={formData.lastName}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                    {(errors.firstName || errors.lastName) && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName || errors.lastName}
                      </p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                        <FiMail className="h-5 w-5" />
                      </div>
                      <input
                        name="email"
                        type="email"
                        required
                        className={`block w-full rounded-lg border ${
                          errors.email ? "border-red-300" : "border-gray-300"
                        } bg-white py-2.5 pl-10 pr-3 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                        placeholder="alexandra@email.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                        <FiLock className="h-5 w-5" />
                      </div>
                      <input
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        className={`block w-full rounded-lg border ${
                          errors.password ? "border-red-300" : "border-gray-300"
                        } bg-white py-2.5 pl-10 pr-10 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((p) => !p)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword ? (
                          <FiEyeOff className="h-5 w-5" />
                        ) : (
                          <FiEye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm password
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                        <FiLock className="h-5 w-5" />
                      </div>
                      <input
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        className={`block w-full rounded-lg border ${
                          errors.confirmPassword
                            ? "border-red-300"
                            : "border-gray-300"
                        } bg-white py-2.5 pl-10 pr-10 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50`}
                        placeholder="••••••••"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((p) => !p)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                        aria-label={
                          showConfirmPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showConfirmPassword ? (
                          <FiEyeOff className="h-5 w-5" />
                        ) : (
                          <FiEye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>

                {/* Terms and Privacy Checkbox */}
                <div className="mt-6 flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      name="terms"
                      type="checkbox"
                      required
                      className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="text-gray-700">
                      By continuing, I accept the{" "}
                      <a
                        href="/terms"
                        className="text-indigo-600 hover:text-indigo-500 underline"
                      >
                        terms of service
                      </a>{" "}
                      and the{" "}
                      <a
                        href="/privacy"
                        className="text-indigo-600 hover:text-indigo-500 underline"
                      >
                        privacy policy
                      </a>
                      .
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-6 w-full rounded-lg py-2.5 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background:
                      "linear-gradient(57.04deg, #5A9AF8 0%, #2A8BF7 100%)",
                  }}
                >
                  {isSubmitting ? "Creating account..." : "Create my account"}
                </button>

                <div className="mt-4 flex items-center justify-center gap-4 text-center text-sm text-gray-600">
                  <div>Already have an account? </div>
                  <div className="flex items-center gap-1">
                    <a
                      href="/login"
                      className="font-medium text-indigo-600 hover:text-indigo-700"
                    >
                      Log in now
                    </a>
                    <ArrowRightIcon className="w-4 h-4" />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <footer className="hidden mb-1 lg:block px-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between text-[12px] text-gray-500">
            <p className="text-[12px]">
              © 2025 ProtectPay. All rights reserved. Licensed escrow service
              provider.
            </p>
            <div className="flex items-center gap-2 text-[12px]">
              <span>Secured by</span>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/60 text-indigo-600">
                <RiShieldCheckFill className="h-4 w-4" />
              </span>
              <span>Escrow level type security</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Register;
